/* eslint-disable @typescript-eslint/no-explicit-any */
import ApiClient from 'api/ApiClient';
import { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { SaveUser } from 'model/user';
import {
    USER_ERROR,
    USER_CLEAR_ERROR,
    USER_CLEAR_SUCCESS,
    USER_RESET,
    USER_SAVE_START,
    USER_SAVE_SUCCESS,
    USER_FETCH_START,
    USER_FETCH_SUCCESS,
    USER_DELETE_START,
    USER_DELETE_SUCCESS,
    UserAction
} from './UserActionsTypes';
import { convertToUser, convertToSaveUserDto } from './UserConverter';
import { UserDto, FetchUsersDto } from './UserDtos';
import { showErrorThunk } from '@spike/notifications-action';

const usersUrl = '/users';

// Extract meaningful error message from API error response
const extractErrorMessage = (apiError: any): string => {
    // Handle Axios error structure
    if (apiError?.response?.data) {
        const { data } = apiError.response;

        // Check for validation errors (422 status) - direct field errors like {"name":["has already been taken"]}
        if (typeof data === 'object' && !data.message && !data.error && !data.errors) {
            // Look for field validation errors directly on data object
            const fieldNames = Object.keys(data);
            if (fieldNames.length > 0) {
                const firstField = fieldNames[0];
                const fieldError = data[firstField];
                if (Array.isArray(fieldError) && fieldError.length > 0) {
                    return `${firstField} ${fieldError[0]}`;
                }
                if (typeof fieldError === 'string') {
                    return `${firstField} ${fieldError}`;
                }
            }
        }

        // Check for validation errors in 'errors' property
        if (data.errors) {
            if (typeof data.errors === 'object') {
                // Extract first error from validation errors object
                const firstField = Object.keys(data.errors)[0];
                const firstError = data.errors[firstField];
                if (Array.isArray(firstError)) {
                    return firstError[0];
                }
                return firstError;
            }
            return data.errors;
        }

        // Check for general error message
        if (data.message) {
            return data.message;
        }

        // Check for error field
        if (data.error) {
            return data.error;
        }
    }

    // Fallback to Axios message or generic error
    if (apiError?.message) {
        return apiError.message;
    }

    // Final fallback
    return 'An unexpected error occurred. Please try again.';
};

const error = (err: string): UserAction => {
    return {
        type: USER_ERROR,
        payload: {
            error: err
        }
    };
};

export const clearUserError = (): UserAction => {
    return {
        type: USER_CLEAR_ERROR
    };
};

export const clearUserSuccess = (): UserAction => {
    return {
        type: USER_CLEAR_SUCCESS
    };
};

export const resetUserState = (): UserAction => {
    return {
        type: USER_RESET
    };
};

/**
 * Fetch Users - GET
 * Retrieves all users
 */
export const fetchUsersThunk = (): ThunkAction<Promise<any[]>, RootState, null, UserAction> => {
    return async dispatch => {
        dispatch({ type: USER_FETCH_START });

        try {
            const { data }: AxiosResponse<FetchUsersDto> = await ApiClient.get(usersUrl);

            // Convert the DTO response to User model array
            const users = Array.isArray(data.users) ? data.users.map(convertToUser) : [];

            dispatch({
                type: USER_FETCH_SUCCESS,
                payload: {
                    users
                }
            });

            return users;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));

            throw apiError;
        }
    };
};

/**
 * Save User - POST (Create)
 * Creates a new user
 */
export const saveUserThunk = (user: SaveUser): ThunkAction<Promise<any>, RootState, null, UserAction> => {
    return async (dispatch): Promise<any> => {
        dispatch({ type: USER_SAVE_START });
        const payload = convertToSaveUserDto(user);
        try {
            // Determine if this is an update or create operation
            const isUpdate = Boolean(user.user.id);
            const url = isUpdate ? `${usersUrl}/${user.user.id}` : usersUrl;
            const apiCall = isUpdate
                ? ApiClient.patch<UserDto>(url, payload, {
                      params: {
                          marketplace_id: user.user.marketplace_id
                      }
                  })
                : ApiClient.post<UserDto>(url, payload);

            const { data }: AxiosResponse<UserDto> = await apiCall;
            const savedUser = convertToUser(data);

            dispatch({
                type: USER_SAVE_SUCCESS,
                payload: { user: savedUser }
            });

            return savedUser;
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};

/**
 * Delete User - DELETE
 * Deletes an existing user
 */
export const deleteUserThunk = (userId: number): ThunkAction<Promise<void>, RootState, null, UserAction> => {
    return async dispatch => {
        dispatch({ type: USER_DELETE_START });

        try {
            const url = `${usersUrl}/${userId}`;
            await ApiClient.delete(url);

            dispatch({
                type: USER_DELETE_SUCCESS,
                payload: {
                    userId
                }
            });
        } catch (apiError) {
            const errorMessage = extractErrorMessage(apiError);
            dispatch(error(errorMessage));
            dispatch(showErrorThunk(errorMessage) as any);

            throw apiError;
        }
    };
};
